<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps<{
  originalYaml?: string
  modifiedYaml?: string
}>()

const lineDelta = computed(() => {
  const before = props.originalYaml?.split('\n').length ?? 0
  const after = props.modifiedYaml?.split('\n').length ?? 0
  const delta = after - before
  if (delta === 0) return 'same line count'
  return delta > 0 ? `+${delta} lines` : `${delta} lines`
})

const changedSteps = computed(() => {
  if (!props.originalYaml || !props.modifiedYaml) return []
  const stepNames = (yaml: string) => {
    const names = new Set<string>()
    for (const line of yaml.split('\n')) {
      const m = line.match(/^\s{2}([a-zA-Z0-9_-]+):\s*$/)
      if (m) names.add(m[1])
    }
    return names
  }
  const before = stepNames(props.originalYaml)
  const after = stepNames(props.modifiedYaml)
  const added = [...after].filter(n => !before.has(n))
  const removed = [...before].filter(n => !after.has(n))
  return [
    ...added.map(n => `+ ${n}`),
    ...removed.map(n => `- ${n}`),
  ].slice(0, 4)
})
</script>

<template>
  <div class="rounded-md border border-border/60 bg-muted/30 p-2 text-xs space-y-1.5 max-w-[300px] min-w-0">
    <div class="flex items-center gap-1.5">
      <p class="font-medium">YAML changes</p>
      <span class="text-[10px] text-muted-foreground">{{ lineDelta }}</span>
    </div>
    <p v-if="changedSteps.length" class="text-muted-foreground font-mono text-[10px] leading-relaxed break-all">
      <span v-for="(step, i) in changedSteps" :key="i" class="block">{{ step }}</span>
    </p>
    <p v-else class="text-muted-foreground text-[10px]">
      {{ originalYaml ? `${originalYaml.split('\n').length} lines before` : '—' }}
      →
      {{ modifiedYaml ? `${modifiedYaml.split('\n').length} lines after` : '—' }}
    </p>
    <p class="text-[10px] text-muted-foreground">Review the diff in the task editor pane.</p>
  </div>
</template>
